import dayjs from 'dayjs';
import { MimeTypeEnum } from 'src/shared/models/enums/mimeType.enum';

export const validFileExtensions = [
  'jpg',
  'jpeg',
  'png',
  'bmp',
  'gif',
  'tif',
  'tiff',
  'psd',
  'ppt',
  'pptx',
  'zip',
  'rar',
  '7z',
  'txt',
  'pdf',
  'doc',
  'docx',
  'xls',
  'xlsx',
  'nda',
];

export function checkFileExtension(filename: string): boolean {
  const fileExtension = filename.split('.').pop()?.toLowerCase();
  return !!validFileExtensions.some((extension) => extension === fileExtension);
}

export const validMimeTypes = [
  MimeTypeEnum.ImageJpeg,
  MimeTypeEnum.ImagePng,
  MimeTypeEnum.ImageGif,
  MimeTypeEnum.ImageSvg,

  MimeTypeEnum.ImageBmp,
  MimeTypeEnum.ImageTiff,

  MimeTypeEnum.Pdf,

  MimeTypeEnum.ExcelXls,
  MimeTypeEnum.ExcelXlsx,

  MimeTypeEnum.WordDoc,
  MimeTypeEnum.WordDocx,

  MimeTypeEnum.Ppt,
  MimeTypeEnum.Pptx,

  MimeTypeEnum.Csv,
  MimeTypeEnum.PlainText,
  MimeTypeEnum.Json,
  MimeTypeEnum.Xml,

  MimeTypeEnum.Zip,

  MimeTypeEnum.Rar,
  MimeTypeEnum.SevenZip,
];

export function checkMimeType(mimeType: string) {
  return !!validMimeTypes.some((mime) => mime === mimeType.toLowerCase());
}

export const getFileAsBase64 = (file) => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = () => resolve(reader.result);
    reader.onerror = (error) => reject(error);
  });
};

// returns base64 without the prefix "ex: data:image/jpeg;base64,"
export const getFileAsBase64WithoutPrefix = (file: File): Promise<string> =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const result = reader.result as string;
      // Ta bort "data:...;base64,"-delen
      const base64 = result.split(',')[1] ?? result;
      resolve(base64);
    };
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });

export const getBase64MimeType = (base64Content) => {
  if (typeof base64Content !== 'string') return null;

  const mime = base64Content.match(/data:([a-zA-Z0-9]+\/[a-zA-Z0-9-.+]+).*,.*/);
  if (mime && mime.length) {
    return mime[1];
  }

  return null;
};

export enum FileValidationModeEnum {
  Normal = 'normal',
  IncomingBankPayment = 'incomingBankPayment',
}

// * Syfte:
//  * - När du bara har en filändelse (t.ex. "png") och behöver veta korrekt type
export const extToMimeMap: Record<string, MimeTypeEnum> = {
  jpg: MimeTypeEnum.ImageJpeg,
  jpeg: MimeTypeEnum.ImageJpeg,
  png: MimeTypeEnum.ImagePng,
  gif: MimeTypeEnum.ImageGif,
  svg: MimeTypeEnum.ImageSvg,
  bmp: MimeTypeEnum.ImageBmp,
  tiff: MimeTypeEnum.ImageTiff,

  pdf: MimeTypeEnum.Pdf,

  xls: MimeTypeEnum.ExcelXls,
  xlsx: MimeTypeEnum.ExcelXlsx,

  doc: MimeTypeEnum.WordDoc,
  docx: MimeTypeEnum.WordDocx,

  ppt: MimeTypeEnum.Ppt,
  pptx: MimeTypeEnum.Pptx,

  csv: MimeTypeEnum.Csv,
  txt: MimeTypeEnum.PlainText,
  json: MimeTypeEnum.Json,
  xml: MimeTypeEnum.Xml,

  zip: MimeTypeEnum.Zip,
  rar: MimeTypeEnum.Rar,
  '7z': MimeTypeEnum.SevenZip,
};

//  * - När du får en Blob/HTTP-response med Content-Type (t.ex. "image/png")
//  *   och vill bygga ett filnamn med rätt filändelse (t.ex. ".png").
export const mimeToExtMap: Record<string, string> = Object.fromEntries(Object.entries(extToMimeMap).map(([ext, mime]) => [mime, ext]));

//  * Sanera filnamn från ogiltiga tecken */
//  * (t.ex. “Rapport: Q1/2025.pdf” → “Rapport_ Q1_2025.pdf”).
const sanitizeFileName = (s: string) => s.replace(/[\\/:*?"<>|]/g, '_');

// Bygg ett filnamn med rätt ändelse utifrån MIME-typ
export const buildDownloadName = (type: MimeTypeEnum | string | undefined, fileName?: string) => {
  const safeBase = sanitizeFileName(fileName || 'file');
  const hasExt = /\.[A-Za-z0-9]+$/.test(safeBase);

  const mimeKey = (type || '').toString().toLowerCase();
  const ext = mimeToExtMap[mimeKey] ?? 'bin';

  if (hasExt) return safeBase;

  return `${safeBase}_${dayjs().format('YYYY-MM-DD_HHmm')}.${ext}`;
};
